// OperatorCompanyHelper.jsx
import React, {
  Fragment,
  useMemo,
  useState
} from "react";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform
} from "framer-motion";
import {
  ArrowRight,
  Binary,
  BookOpenCheck,
  Bot,
  BrainCircuit,
  Check,
  ChevronDown,
  Compass,
  Eye,
  Fingerprint,
  Flag,
  Gamepad2,
  HeartHandshake,
  Layers3,
  LifeBuoy,
  Network,
  Orbit,
  Scale,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
  WalletCards,
  Workflow,
  X
} from "lucide-react";
import "./Styles.css";
import HeaderToMainPage from "../Universal/Header/Index";
import Footer from "../Universal/Footer/Index";
import { Link } from "react-router-dom";


/* ============================================================
   COPY / CONTENT
============================================================ */

const originPhases = [
  {
    id: "before",
    index: "01",
    kicker: "THE QUESTION THAT STARTED IT",
    title: "Why build another gaming platform at all?",
    accent: "cyan",
    icon: Compass,
    summary: "Ethosino began with a founder asking whether gaming software could be more understandable and more accountable.",
    body: "The initial motivation was not to launch a copy of an existing casino or resell a catalog. It was to work from the inside out: understand the rules behind a game, the experience around it and the responsibilities created when software handles player activity. That question became a long-term engineering project rather than a quick front-end experiment."
  },
  {
    id: "principles",
    index: "02",
    kicker: "A DIFFERENT BUILDING DECISION",
    title: "Build the underlying systems, not just the storefront.",
    accent: "green",
    icon: Fingerprint,
    summary: "The project grew because an interface alone could not carry the responsibilities of an operator.",
    body: "An early prototype can make gameplay look convincing. Building a maintainable product means answering less visible questions about state, administration, money, games and support. Ethosino developed those components as part of one codebase and operating model, with first-party engineering at the center of the work rather than a collection of unrelated vendor screens."
  },
  {
    id: "player",
    index: "03",
    kicker: "FROM PROJECT TO B2B PRODUCT",
    title: "The operator became the customer.",
    accent: "violet",
    icon: HeartHandshake,
    summary: "The opportunity expanded from individual gaming experiences into software other brands could operate.",
    body: "The more complete the game and administration layer became, the more obvious the B2B use case was. A business bringing an audience and a market strategy should not have to reproduce years of platform engineering to test an idea. Ethosino shifted toward configurable operator environments, a sales demonstration and a practical path for bringing an initial partner onto the software."
  },
  {
    id: "fruit",
    index: "04",
    kicker: "THE STAGE WE ARE IN",
    title: "Built enough to demonstrate. Ready to earn deployment trust.",
    accent: "pink",
    icon: Layers3,
    summary: "The next milestone is not another feature count; it is working closely with a real operator through a scoped pilot.",
    body: "Ethosino has a substantial demonstrable product, including a player-facing environment and operator administration. A commercial launch still depends on the partner’s legal review, provider approvals, operating decisions and production validation. The company’s immediate focus is turning hands-on demonstrations into a carefully scoped pilot and learning from genuine operator workflows rather than treating the existence of a demo as proof of market adoption."
  }
];


const values = [
  {
    icon: ShieldCheck,
    label: "ENGINEERING OWNERSHIP",
    title: "Know the software we ship.",
    body: "Core platform behavior is developed with direct knowledge of its dependencies and tradeoffs. It is easier to maintain, improve and explain something the company actually understands."
  },
  {
    icon: Eye,
    label: "SPECIFIC COMMUNICATION",
    title: "Make claims somebody can test.",
    body: "A screenshot, a demo, an integration plan and a production deployment are different things. We aim to describe each accurately instead of letting polished presentation imply completed work."
  },
  {
    icon: HeartHandshake,
    label: "PARTNER RELATIONSHIPS",
    title: "Listen before prescribing.",
    body: "An operator brings its own audience, commercial model and constraints. Early partnerships should shape priorities through concrete feedback rather than forcing every business into one predetermined configuration."
  },
  {
    icon: LifeBuoy,
    label: "PRODUCT CARE",
    title: "Finish the unglamorous work.",
    body: "Support journeys, failure states, edge cases and administrative usability matter as much as a striking lobby. A product is not ready simply because its happiest path looks good."
  },
  {
    icon: Scale,
    label: "CANDID LIMITATIONS",
    title: "Be explicit about what remains external.",
    body: "Ethosino is a software provider, not a payment processor, regulator or legal adviser. Market access and third-party approvals require separate people, contracts and decisions."
  },
  {
    icon: Workflow,
    label: "LONG-TERM THINKING",
    title: "Improve a platform, not a disposable launch.",
    body: "Prioritize maintainable systems and shared improvements that can benefit future operators, while retaining room for each partner to configure its own experience and operations."
  }
];


const playerFirstPrinciples = [
  {
    id: "clarity",
    number: "01",
    icon: BookOpenCheck,
    title: "Demonstrate before asking for belief",
    short: "Let prospects inspect the product.",
    body: "The sales process should let an operator navigate the player experience, inspect relevant administration and ask for real workflows to be shown. A beautifully designed presentation is an introduction, not the evidence that the underlying capability works.",
    telemetry: [
      ["ASK", "SHOW IT"],
      ["METHOD", "DEMO"],
      ["STANDARD", "SPECIFIC"]
    ]
  },
  {
    id: "proof",
    number: "02",
    icon: Fingerprint,
    title: "Name the remaining launch dependencies",
    short: "Separate software from approval.",
    body: "A working tenant is not the same as an approved sweepstakes business. Legal opinions, payments, identity services and production requirements are evaluated with the prospective operator. The company should identify those dependencies early instead of presenting them as solved by the software alone.",
    telemetry: [
      ["SCOPE", "CLEAR"],
      ["PROVIDERS", "EXTERNAL"],
      ["STATUS", "EXPLICIT"]
    ]
  },
  {
    id: "control",
    number: "03",
    icon: LifeBuoy,
    title: "Treat the operator as a design partner",
    short: "Let real use reshape priorities.",
    body: "In an early pilot, useful feedback is specific: where onboarding confuses people, which administration task takes too long, what a support agent cannot see, and which integrations block the business. These observations should have more influence than speculative feature lists.",
    telemetry: [
      ["INPUT", "OPERATOR"],
      ["TEST", "WORKFLOWS"],
      ["OUTPUT", "ITERATE"]
    ]
  },
  {
    id: "integrity",
    number: "04",
    icon: WalletCards,
    title: "Protect the experience as it evolves",
    short: "Keep quality ahead of novelty.",
    body: "New games, richer interactions and AI features are valuable only when the basics remain dependable. The company’s design standard is to make releases testable, preserve existing behavior where possible and improve the product based on what actually happens in use.",
    telemetry: [
      ["RELEASE", "TESTABLE"],
      ["CHANGE", "REVIEWED"],
      ["GOAL", "RELIABLE"]
    ]
  }
];


const buildJourney = [
  {
    year: "QUESTION",
    icon: Compass,
    title: "An independent engineering thesis",
    body: "Begin with the conviction that the internals of a gaming experience matter as much as its presentation."
  },
  {
    year: "GAME STUDIO",
    icon: Binary,
    title: "Create first-party gameplay",
    body: "Develop original mechanics and table experiences to learn the full path from interaction to outcome."
  },
  {
    year: "BACK OFFICE",
    icon: Layers3,
    title: "Build the parts an operator uses",
    body: "Extend the work into player administration, finance-related workflows, reporting and support surfaces."
  },
  {
    year: "MULTI-TENANT",
    icon: Network,
    title: "Make the product usable by distinct brands",
    body: "Shift from a single branded experience to configurable environments built on a maintained shared application."
  },
  {
    year: "OPEN DEMO",
    icon: BrainCircuit,
    title: "Put the product in front of outsiders",
    body: "Make the player-facing software and sales materials accessible so prospects can ask informed questions."
  },
  {
    year: "NEXT: PILOT",
    icon: Target,
    title: "Validate with an operating partner",
    body: "Scope an initial deployment, test real operator workflows and prioritize changes from demonstrated needs."
  }
];


const truthPairs = [
  {
    left: "We built beyond a clickable prototype.",
    right: "That still does not replace production acceptance testing."
  },
  {
    left: "A first-party catalog gives more control.",
    right: "It does not remove the need for player demand or distribution."
  },
  {
    left: "A shared product can serve multiple brands.",
    right: "Every operator still deserves a clearly agreed scope."
  },
  {
    left: "A polished demo makes evaluation easier.",
    right: "It should not be confused with proven commercial traction."
  },
  {
    left: "Ambitious engineering creates possibilities.",
    right: "Real-world feedback decides what deserves priority next."
  }
];


const faqItems = [
  {
    q: "Who is building Ethosino?",
    a: "Ethosino is founder-led and has been developed primarily through independent full-stack engineering. The company has built its own game experiences and operator software rather than starting as a reseller of an existing white-label casino. As the product moves toward commercial deployments, the right specialist partnerships and external services remain important."
  },
  {
    q: "Why did the project become a B2B platform?",
    a: "The engineering work grew beyond games into administration, accounts, configuration, payments-related workflows and operating tools. Those capabilities suggested a more useful business: providing infrastructure to operators with their own brand, audience and launch plan rather than competing for the same end players."
  },
  {
    q: "Is Ethosino already operating live casinos for customers?",
    a: "The platform has a public demonstration environment and extensive operator-facing software. A demo should not be mistaken for a publicly verified roster of live customers. Commercial onboarding, third-party integration approval and production validation are handled as separate deployment milestones."
  },
  {
    q: "What does “built in-house” mean?",
    a: "It means important parts of Ethosino’s product—such as the application experience, first-party games and administration—were engineered as part of the company’s own software. It does not mean every external service is proprietary: cloud hosting, payments, identity checks, location data and potential third-party game providers involve independent vendors."
  },
  {
    q: "What kind of early operator partnership are you seeking?",
    a: "An engaged operator with a clear audience and market plan, willing to test realistic workflows, define legal and provider requirements, and give direct feedback. Early scope should be explicit: what is included, what still needs integration and which milestones would justify moving from a pilot to broader operation."
  },
  {
    q: "How does Ethosino decide what to build next?",
    a: "The immediate priority is practical deployment readiness: removing friction from onboarding, hardening operational workflows, completing required integrations and learning from pilot use. New features remain possible, but they should answer real operator needs rather than exist only to expand a public feature count."
  },
  {
    q: "What is the longer-term company direction?",
    a: "Build a durable B2B gaming software company with an increasingly strong first-party product, reliable operator tools and partner relationships. The measure of progress will be successful, sustainable customer deployments and the quality of the software over time—not just the size of the demo catalog."
  }
];


/* ============================================================
   MOTION
============================================================ */

const Reveal = ({
  children,
  className = "",
  direction = "up",
  delay = 0
}) => {
  const reduceMotion =
    useReducedMotion();

  const starts = {
    left: {
      x: -120,
      y: 0,
      rotate: -1.6
    },
    right: {
      x: 120,
      y: 0,
      rotate: 1.6
    },
    up: {
      x: 0,
      y: 75,
      rotate: 0
    },
    topLeft: {
      x: -150,
      y: -105,
      rotate: -2.5
    },
    topRight: {
      x: 150,
      y: -105,
      rotate: 2.5
    },
    bottomLeft: {
      x: -150,
      y: 110,
      rotate: -2.5
    },
    bottomRight: {
      x: 150,
      y: 110,
      rotate: 2.5
    }
  };

  const start =
    starts[direction] ||
    starts.up;

  return (
    <motion.div
      className={
        className
      }
      initial={
        reduceMotion
          ? false
          : {
              opacity:
                0,
              ...start,
              scale:
                0.95,
              filter:
                "blur(10px)"
            }
      }
      whileInView={{
        opacity:
          1,
        x:
          0,
        y:
          0,
        rotate:
          0,
        scale:
          1,
        filter:
          "blur(0px)"
      }}
      viewport={{
        once:
          true,
        amount:
          0.14
      }}
      transition={{
        duration:
          reduceMotion
            ? 0
            : 0.84,
        delay,
        ease: [
          0.16,
          1,
          0.3,
          1
        ]
      }}
    >
      {
        children
      }
    </motion.div>
  );
};


const Spotlight = ({
  children,
  className = ""
}) => {
  const handlePointerMove =
    event => {
      const rect =
        event.currentTarget.getBoundingClientRect();

      event.currentTarget.style.setProperty(
        "--ea9-mx",
        `${event.clientX - rect.left}px`
      );

      event.currentTarget.style.setProperty(
        "--ea9-my",
        `${event.clientY - rect.top}px`
      );
    };

  return (
    <div
      className={`eth-about-v9-spotlight ${className}`}
      onPointerMove={
        handlePointerMove
      }
    >
      <div className="eth-about-v9-spotlight-glow" />
      {
        children
      }
    </div>
  );
};


/* ============================================================
   VISION ORBIT
============================================================ */

const VisionOrbit = ({
  reduceMotion
}) => {
  const orbitNodes = [
    {
      className:
        "eth-about-v9-orbit-node--fairness",
      icon:
        Fingerprint,
      label:
        "IN-HOUSE",
      value:
        "BUILD"
    },
    {
      className:
        "eth-about-v9-orbit-node--player",
      icon:
        HeartHandshake,
      label:
        "DESIGN",
      value:
        "DETAIL"
    },
    {
      className:
        "eth-about-v9-orbit-node--operator",
      icon:
        Layers3,
      label:
        "PARTNER",
      value:
        "LISTEN"
    },
    {
      className:
        "eth-about-v9-orbit-node--evidence",
      icon:
        Eye,
      label:
        "PRODUCT",
      value:
        "DEMO"
    },
    {
      className:
        "eth-about-v9-orbit-node--responsibility",
      icon:
        LifeBuoy,
      label:
        "NEXT STEP",
      value:
        "PILOT"
    }
  ];

  return (
    <div className="eth-about-v9-vision-orbit">

      <div className="eth-about-v9-vision-orbit-grid" />

      <div className="eth-about-v9-vision-orbit-scan" />


      <div className="eth-about-v9-vision-orbit-top">
        <div>
          <span className="eth-about-v9-live-dot" />

          FOUNDER-LED / DEVELOPING
        </div>

        <b>
          IDEA → SOFTWARE
        </b>
      </div>


      <div className="eth-about-v9-orbit-field">

        <motion.div
          className="eth-about-v9-orbit-ring eth-about-v9-orbit-ring--outer"
          animate={
            reduceMotion
              ? {}
              : {
                  rotate:
                    360
                }
          }
          transition={{
            duration:
              35,
            repeat:
              Infinity,
            ease:
              "linear"
          }}
        />

        <motion.div
          className="eth-about-v9-orbit-ring eth-about-v9-orbit-ring--middle"
          animate={
            reduceMotion
              ? {}
              : {
                  rotate:
                    -360
                }
          }
          transition={{
            duration:
              25,
            repeat:
              Infinity,
            ease:
              "linear"
          }}
        />

        <motion.div
          className="eth-about-v9-orbit-ring eth-about-v9-orbit-ring--inner"
          animate={
            reduceMotion
              ? {}
              : {
                  rotate:
                    360
                }
          }
          transition={{
            duration:
              18,
            repeat:
              Infinity,
            ease:
              "linear"
          }}
        />


        <div className="eth-about-v9-orbit-core">

          <span className="eth-about-v9-orbit-wave" />

          <Compass
            size={29}
            strokeWidth={1.7}
          />

          <small>
            ETHOSINO
          </small>

          <strong>
            THE BUILD
          </strong>

          <b>
            ENGINEERING FIRST
          </b>

        </div>


        {
          orbitNodes.map(
            (
              node,
              index
            ) => {
              const Icon =
                node.icon;

              return (
                <motion.div
                  key={
                    node.label
                  }
                  className={`eth-about-v9-orbit-node ${node.className}`}
                  animate={
                    reduceMotion
                      ? {}
                      : {
                          y: [
                            0,
                            index %
                            2 ===
                            0
                              ? -9
                              : 9,
                            0
                          ],
                          x: [
                            0,
                            index %
                            3 ===
                            0
                              ? 5
                              : -5,
                            0
                          ]
                        }
                  }
                  transition={{
                    duration:
                      4.6 +
                      index *
                      0.31,
                    repeat:
                      Infinity,
                    ease:
                      "easeInOut"
                  }}
                >
                  <Icon
                    size={15}
                  />

                  <div>
                    <span>
                      {
                        node.label
                      }
                    </span>

                    <strong>
                      {
                        node.value
                      }
                    </strong>
                  </div>
                </motion.div>
              );
            }
          )
        }


        <span className="eth-about-v9-vision-packet eth-about-v9-vision-packet--one" />

        <span className="eth-about-v9-vision-packet eth-about-v9-vision-packet--two" />

        <span className="eth-about-v9-vision-packet eth-about-v9-vision-packet--three" />

      </div>


      <div className="eth-about-v9-vision-orbit-bottom">

        <div>
          <span>
            ORIGINAL QUESTION
          </span>

          <strong>
            WHY BUILD IT?
          </strong>
        </div>

        <ArrowRight
          size={16}
        />

        <div>
          <span>
            CURRENT STAGE
          </span>

          <strong>
            DEMO + PILOT PREPARATION
          </strong>
        </div>

        <b>
          DEMO AVAILABLE
        </b>

      </div>

    </div>
  );
};


/* ============================================================
   PAGE
============================================================ */

const OperatorCompanyHelper = () => {
  const reduceMotion =
    useReducedMotion();

  const [
    activePhase,
    setActivePhase
  ] =
    useState(
      0
    );

  const [
    activePlayerPrinciple,
    setActivePlayerPrinciple
  ] =
    useState(
      0
    );

  const [
    openFaq,
    setOpenFaq
  ] =
    useState(
      0
    );


  const currentPhase =
    originPhases[
      activePhase
    ];

  const CurrentPhaseIcon =
    currentPhase.icon;


  const currentPlayerPrinciple =
    playerFirstPrinciples[
      activePlayerPrinciple
    ];

  const CurrentPlayerIcon =
    currentPlayerPrinciple.icon;


  const {
    scrollYProgress
  } =
    useScroll();


  const progress =
    useSpring(
      scrollYProgress,
      {
        stiffness:
          120,
        damping:
          28,
        restDelta:
          0.001
      }
    );


  const heroY =
    useTransform(
      scrollYProgress,
      [
        0,
        0.16
      ],
      [
        0,
        reduceMotion
          ? 0
          : 72
      ]
    );


  const heroOpacity =
    useTransform(
      scrollYProgress,
      [
        0,
        0.13
      ],
      [
        1,
        0.77
      ]
    );


  const tickerItems =
    useMemo(
      () => [
        "FOUNDER-LED PRODUCT DEVELOPMENT",
        "SUBSTANTIVE SOFTWARE BEFORE SUPERLATIVES",
        "BUILD THE DETAILS THAT OPERATORS NEED",
        "PARTNERSHIP BEGINS WITH LISTENING",
        "DESCRIBE THE PRODUCT AS IT ACTUALLY EXISTS",
        "FIRST-PARTY GAMES AND OPERATOR SOFTWARE",
        "DEMO TODAY • PILOT NEXT",
        "KEEP BUILDING WITH REAL FEEDBACK"
      ],
      []
    );


  return (
    <Fragment>

      <motion.div
        className="eth-about-v9-scroll-progress"
        style={{
          scaleX:
            progress
        }}
      />


      <HeaderToMainPage />


      <main
        className="eth-about-v9-page eth-about-v9-readable"
        id="ethosino-about-v9"
      >

        {/* ====================================================
            ATMOSPHERE
        ==================================================== */}

        <div
          className="eth-about-v9-atmosphere"
          aria-hidden="true"
        >
          <div className="eth-about-v9-atmosphere-grid" />

          <div className="eth-about-v9-atmosphere-noise" />

          <div className="eth-about-v9-atmosphere-line eth-about-v9-atmosphere-line--one" />

          <div className="eth-about-v9-atmosphere-line eth-about-v9-atmosphere-line--two" />

          <div className="eth-about-v9-orb eth-about-v9-orb--cyan" />

          <div className="eth-about-v9-orb eth-about-v9-orb--green" />

          <div className="eth-about-v9-orb eth-about-v9-orb--violet" />

          <div className="eth-about-v9-orb eth-about-v9-orb--pink" />
        </div>


        {/* ====================================================
            HERO
        ==================================================== */}

        <section
          className="eth-about-v9-hero"
          id="about-top"
        >

          <motion.div
            className="eth-about-v9-hero-parallax"
            style={{
              y:
                heroY,
              opacity:
                heroOpacity
            }}
          >

            <div className="eth-about-v9-shell eth-about-v9-hero-grid">

              <div className="eth-about-v9-hero-copy">

                <motion.div
                  className="eth-about-v9-system-badge"
                  initial={
                    reduceMotion
                      ? false
                      : {
                          opacity:
                            0,
                          x:
                            -38
                        }
                  }
                  animate={{
                    opacity:
                      1,
                    x:
                      0
                  }}
                  transition={{
                    duration:
                      0.58
                  }}
                >
                  <span className="eth-about-v9-live-dot" />

                  THE COMPANY

                  <i />

                  THE STORY BEHIND ETHOSINO
                </motion.div>


                <motion.h1
                  initial={
                    reduceMotion
                      ? false
                      : {
                          opacity:
                            0,
                          x:
                            -110,
                          y:
                            44,
                          rotate:
                            -2
                        }
                  }
                  animate={{
                    opacity:
                      1,
                    x:
                      0,
                    y:
                      0,
                    rotate:
                      0
                  }}
                  transition={{
                    duration:
                      0.86,
                    delay:
                      0.04,
                    ease: [
                      0.16,
                      1,
                      0.3,
                      1
                    ]
                  }}
                >
                  An independent idea.

                  <span>
                    A platform built from the inside out.
                  </span>
                </motion.h1>


                <motion.p
                  className="eth-about-v9-hero-lead"
                  initial={
                    reduceMotion
                      ? false
                      : {
                          opacity:
                            0,
                          y:
                            30
                        }
                  }
                  animate={{
                    opacity:
                      1,
                    y:
                      0
                  }}
                  transition={{
                    duration:
                      0.72,
                    delay:
                      0.14
                  }}
                >
                  Ethosino is a founder-led software company built around a straightforward conviction: serious gaming technology should be engineered as a coherent product, not assembled as a collection of attractive screens. What began with individual game systems grew into a platform that prospective operators can explore, evaluate and help shape.
                </motion.p>


                <motion.div
                  className="eth-about-v9-hero-thesis"
                  initial={
                    reduceMotion
                      ? false
                      : {
                          opacity:
                            0,
                          x:
                            -46
                        }
                  }
                  animate={{
                    opacity:
                      1,
                    x:
                      0
                  }}
                  transition={{
                    duration:
                      0.67,
                    delay:
                      0.24
                  }}
                >
                  <HeartHandshake
                    size={18}
                  />

                  <span>
                    The goal was never <strong>to resell a ready-made casino.</strong>{" "}
                    It was to <strong>build the underlying product, demonstrate it and improve it alongside operators.</strong>
                  </span>
                </motion.div>


                <motion.div
                  className="eth-about-v9-hero-actions"
                  initial={
                    reduceMotion
                      ? false
                      : {
                          opacity:
                            0,
                          y:
                            24
                        }
                  }
                  animate={{
                    opacity:
                      1,
                    y:
                      0
                  }}
                  transition={{
                    duration:
                      0.62,
                    delay:
                      0.31
                  }}
                >

                  <a
                    href="#about-origin"
                    className="eth-about-v9-btn eth-about-v9-btn--primary"
                  >
                    How it began

                    <ArrowRight
                      size={17}
                    />
                  </a>


                  <Link
                    to="/book-demo"
                    className="eth-about-v9-btn eth-about-v9-btn--ghost"
                  >
                    Explore a live demonstration
                  </Link>

                </motion.div>


                <div className="eth-about-v9-hero-proofline">

                  <span>
                    <Fingerprint
                      size={14}
                    />
                    founder-led engineering
                  </span>

                  <span>
                    <HeartHandshake
                      size={14}
                    />
                    first-party product
                  </span>

                  <span>
                    <LifeBuoy
                      size={14}
                    />
                    hands-on demos
                  </span>

                  <span>
                    <Eye
                      size={14}
                    />
                    operator feedback
                  </span>

                  <span>
                    <Layers3
                      size={14}
                    />
                    long-term platform thinking
                  </span>

                </div>

              </div>


              <motion.div
                className="eth-about-v9-hero-visual"
                initial={
                  reduceMotion
                    ? false
                    : {
                        opacity:
                          0,
                        x:
                          120,
                        y:
                          -62,
                        rotate:
                          2.5,
                        scale:
                          0.94
                      }
                }
                animate={{
                  opacity:
                    1,
                  x:
                    0,
                  y:
                    0,
                  rotate:
                    0,
                  scale:
                    1
                }}
                transition={{
                  duration:
                    0.96,
                  delay:
                    0.12,
                  ease: [
                    0.16,
                    1,
                    0.3,
                    1
                  ]
                }}
              >

                <VisionOrbit
                  reduceMotion={
                    reduceMotion
                  }
                />


                <motion.div
                  className="eth-about-v9-float eth-about-v9-float--one"
                  animate={
                    reduceMotion
                      ? {}
                      : {
                          y: [
                            0,
                            -12,
                            0
                          ],
                          rotate: [
                            -1,
                            1,
                            -1
                          ]
                        }
                  }
                  transition={{
                    duration:
                      5.6,
                    repeat:
                      Infinity,
                    ease:
                      "easeInOut"
                  }}
                >
                  <Compass
                    size={15}
                  />

                  <div>
                    <span>
                      ORIGINAL CHOICE
                    </span>

                    <strong>
                      BUILD IN-HOUSE
                    </strong>
                  </div>
                </motion.div>


                <motion.div
                  className="eth-about-v9-float eth-about-v9-float--two"
                  animate={
                    reduceMotion
                      ? {}
                      : {
                          y: [
                            0,
                            11,
                            0
                          ],
                          rotate: [
                            1,
                            -1,
                            1
                          ]
                        }
                  }
                  transition={{
                    duration:
                      6.4,
                    repeat:
                      Infinity,
                    ease:
                      "easeInOut"
                  }}
                >
                  <Layers3
                    size={15}
                  />

                  <div>
                    <span>
                      CURRENT FOCUS
                    </span>

                    <strong>
                      FIRST OPERATOR PILOTS
                    </strong>
                  </div>
                </motion.div>

              </motion.div>

            </div>

          </motion.div>


          <div className="eth-about-v9-ticker">
            <div className="eth-about-v9-ticker-track">

              {
                [
                  ...tickerItems,
                  ...tickerItems
                ].map(
                  (
                    item,
                    index
                  ) => (
                    <React.Fragment
                      key={`${item}-${index}`}
                    >
                      <span>
                        {
                          item
                        }
                      </span>

                      <i>
                        ◆
                      </i>
                    </React.Fragment>
                  )
                )
              }

            </div>
          </div>

        </section>


        {/* ====================================================
            SUBNAV
        ==================================================== */}

        <nav className="eth-about-v9-subnav">

          <div className="eth-about-v9-shell eth-about-v9-subnav-inner">

            <div className="eth-about-v9-subnav-brand">
              <span />

              THE COMPANY STORY
            </div>


            <div className="eth-about-v9-subnav-links">

              <a href="#about-origin">
                Beginnings
              </a>

              <a href="#about-values">
                Approach
              </a>

              <a href="#about-player-first">
                Commitments
              </a>

              <a href="#about-journey">
                Progress
              </a>

              <a href="#about-future">
                Direction
              </a>

              <a href="#about-faq">
                FAQ
              </a>

              <Link
                className="eth-about-v9-subnav-cta"
                to="/book-demo"
              >
                Demo
              </Link>

            </div>

          </div>

        </nav>


        {/* ====================================================
            ORIGIN
        ==================================================== */}

        <section
          className="eth-about-v9-section"
          id="about-origin"
        >

          <div className="eth-about-v9-shell">

            <Reveal
              direction="topLeft"
              className="eth-about-v9-section-head eth-about-v9-section-head--split"
            >
              <div>
                <span className="eth-about-v9-kicker">
                  HOW ETHOSINO STARTED
                </span>

                <h2>
                  Not a resold platform.{" "}
                  <span>
                    An independently built product.
                  </span>
                </h2>
              </div>

              <p>
                The company began with software development, not a distribution deal or a prepackaged casino skin. As the original game ideas became more capable, the work expanded into the less visible tools needed to support an actual business. That progression—from building individual experiences to serving independent operators—is what the story below traces.
              </p>
            </Reveal>


            <div className="eth-about-v9-origin-layout">

              <Reveal
                direction="left"
                className="eth-about-v9-origin-tabs"
              >

                {
                  originPhases.map(
                    (
                      phase,
                      index
                    ) => {
                      const Icon =
                        phase.icon;

                      return (
                        <motion.button
                          key={
                            phase.id
                          }
                          type="button"
                          className={[
                            "eth-about-v9-origin-tab",
                            `eth-about-v9-origin-tab--${phase.accent}`,
                            activePhase === index
                              ? "is-active"
                              : ""
                          ].join(
                            " "
                          )}
                          onClick={() =>
                            setActivePhase(
                              index
                            )
                          }
                          whileHover={{
                            x:
                              5
                          }}
                          whileTap={{
                            scale:
                              0.985
                          }}
                        >

                          <span className="eth-about-v9-origin-tab-index">
                            {
                              phase.index
                            }
                          </span>

                          <span className="eth-about-v9-origin-tab-icon">
                            <Icon
                              size={18}
                            />
                          </span>

                          <span className="eth-about-v9-origin-tab-copy">
                            <small>
                              {
                                phase.kicker
                              }
                            </small>

                            <strong>
                              {
                                phase.title
                              }
                            </strong>

                            <em>
                              {
                                phase.summary
                              }
                            </em>
                          </span>

                          <ArrowRight
                            size={15}
                          />

                        </motion.button>
                      );
                    }
                  )
                }

              </Reveal>


              <Reveal
                direction="right"
                className="eth-about-v9-origin-detail-wrap"
              >

                <AnimatePresence
                  mode="wait"
                >
                  <motion.article
                    key={
                      currentPhase.id
                    }
                    className={`eth-about-v9-origin-detail eth-about-v9-origin-detail--${currentPhase.accent}`}
                    initial={
                      reduceMotion
                        ? false
                        : {
                            opacity:
                              0,
                            x:
                              78,
                            y:
                              -28,
                            rotate:
                              1.4,
                            filter:
                              "blur(8px)"
                          }
                    }
                    animate={{
                      opacity:
                        1,
                      x:
                        0,
                      y:
                        0,
                      rotate:
                        0,
                      filter:
                        "blur(0px)"
                    }}
                    exit={
                      reduceMotion
                        ? undefined
                        : {
                            opacity:
                              0,
                            x:
                              -62,
                            y:
                              25,
                            rotate:
                              -1.2,
                            filter:
                              "blur(7px)"
                          }
                    }
                    transition={{
                      duration:
                        0.34
                    }}
                  >

                    <div className="eth-about-v9-origin-detail-top">

                      <span className="eth-about-v9-origin-big-index">
                        {
                          currentPhase.index
                        }
                      </span>

                      <div className="eth-about-v9-origin-detail-icon">
                        <CurrentPhaseIcon
                          size={28}
                        />
                      </div>

                    </div>


                    <small>
                      {
                        currentPhase.kicker
                      }
                    </small>

                    <h3>
                      {
                        currentPhase.title
                      }
                    </h3>

                    <p className="eth-about-v9-origin-summary">
                      {
                        currentPhase.summary
                      }
                    </p>

                    <p className="eth-about-v9-origin-body">
                      {
                        currentPhase.body
                      }
                    </p>


                    <div className="eth-about-v9-origin-rail">

                      <span>
                        QUESTION
                      </span>

                      <ArrowRight
                        size={14}
                      />

                      <span>
                        ENGINEERING
                      </span>

                      <ArrowRight
                        size={14}
                      />

                      <span>
                        PRODUCT
                      </span>

                      <ArrowRight
                        size={14}
                      />

                      <span>
                        PARTNERSHIP
                      </span>

                    </div>

                  </motion.article>
                </AnimatePresence>

              </Reveal>

            </div>

          </div>
        </section>


        {/* ====================================================
            MANIFESTO
        ==================================================== */}

        <section className="eth-about-v9-section eth-about-v9-section--panel">

          <div className="eth-about-v9-shell">

            <Reveal
              direction="bottomRight"
              className="eth-about-v9-manifesto"
            >

              <div className="eth-about-v9-manifesto-grid" />


              <div className="eth-about-v9-manifesto-icon">
                <Flag
                  size={25}
                />
              </div>


              <div className="eth-about-v9-manifesto-copy">

                <span>
                  WHAT WE BELIEVE ABOUT BUILDING
                </span>

                <h2>
                  A product deserves more than{" "}
                  <strong>
                    a convincing sales presentation.
                  </strong>
                </h2>

                <p>
                  The standard is not whether a system looks complete at a glance. It is whether the team can explain its behavior, demonstrate realistic workflows, identify unfinished dependencies and improve the work when a partner finds a problem. Those principles guide Ethosino as it approaches its first operator deployments.
                </p>

              </div>


              <div className="eth-about-v9-manifesto-code">

                <div>
                  <span>
                    product
                  </span>

                  <strong>
                    demonstrable
                  </strong>
                </div>

                <div>
                  <span>
                    scope
                  </span>

                  <strong>
                    explicit
                  </strong>
                </div>

                <div>
                  <span>
                    feedback
                  </span>

                  <strong>
                    actionable
                  </strong>
                </div>

                <div>
                  <span>
                    progress
                  </span>

                  <strong>
                    earned
                  </strong>
                </div>

              </div>

            </Reveal>

          </div>
        </section>


        {/* ====================================================
            VALUES
        ==================================================== */}

        <section
          className="eth-about-v9-section"
          id="about-values"
        >

          <div className="eth-about-v9-shell">

            <Reveal
              direction="topRight"
              className="eth-about-v9-section-head"
            >
              <span className="eth-about-v9-kicker">
                THE WAY WE CHOOSE TO BUILD
              </span>

              <h2>
                A small team can have{" "}
                <span>
                  clear engineering principles.
                </span>
              </h2>

              <p>
                Ethosino has been built with unusually broad ownership of its own software. That independence brings freedom, but it also creates an obligation to be candid about what has been tested, where third-party services are required and what an operator needs before going live. These are the working standards behind the company.
              </p>
            </Reveal>


            <div className="eth-about-v9-values-grid">

              {
                values.map(
                  (
                    value,
                    index
                  ) => {
                    const Icon =
                      value.icon;

                    const directions = [
                      "topLeft",
                      "topRight",
                      "left",
                      "right",
                      "bottomLeft",
                      "bottomRight"
                    ];

                    return (
                      <Reveal
                        key={
                          value.label
                        }
                        direction={
                          directions[
                            index
                          ]
                        }
                        delay={
                          index *
                          0.045
                        }
                      >
                        <Spotlight className="eth-about-v9-value-card">

                          <div className="eth-about-v9-value-card-top">
                            <span>
                              0{
                                index +
                                1
                              }
                            </span>

                            <Icon
                              size={20}
                            />
                          </div>

                          <small>
                            {
                              value.label
                            }
                          </small>

                          <h3>
                            {
                              value.title
                            }
                          </h3>

                          <p>
                            {
                              value.body
                            }
                          </p>

                        </Spotlight>
                      </Reveal>
                    );
                  }
                )
              }

            </div>

          </div>
        </section>


        {/* ====================================================
            PLAYER FIRST
        ==================================================== */}

        <section
          className="eth-about-v9-section eth-about-v9-section--panel"
          id="about-player-first"
        >

          <div className="eth-about-v9-shell">

            <Reveal
              direction="bottomLeft"
              className="eth-about-v9-section-head eth-about-v9-section-head--split"
            >
              <div>
                <span className="eth-about-v9-kicker">
                  WHAT WE WANT PARTNERS TO EXPERIENCE
                </span>

                <h2>
                  A better first meeting{" "}
                  <span>
                    starts with a working product.
                  </span>
                </h2>
              </div>

              <p>
                A prospective operator should leave a conversation with more than a feature list. They should know what they can explore today, which workflows have been demonstrated, what the launch prerequisites are and how feedback could influence the product. Those commitments shape our approach to early partnerships.
              </p>
            </Reveal>


            <div className="eth-about-v9-player-layout">

              <Reveal
                direction="left"
                className="eth-about-v9-player-tabs"
              >

                {
                  playerFirstPrinciples.map(
                    (
                      principle,
                      index
                    ) => {
                      const Icon =
                        principle.icon;

                      return (
                        <motion.button
                          key={
                            principle.id
                          }
                          type="button"
                          className={[
                            "eth-about-v9-player-tab",
                            activePlayerPrinciple === index
                              ? "is-active"
                              : ""
                          ].join(
                            " "
                          )}
                          onClick={() =>
                            setActivePlayerPrinciple(
                              index
                            )
                          }
                          whileHover={{
                            x:
                              5
                          }}
                          whileTap={{
                            scale:
                              0.985
                          }}
                        >

                          <span>
                            {
                              principle.number
                            }
                          </span>

                          <div className="eth-about-v9-player-tab-icon">
                            <Icon
                              size={18}
                            />
                          </div>

                          <div className="eth-about-v9-player-tab-copy">
                            <small>
                              {
                                principle.short
                              }
                            </small>

                            <strong>
                              {
                                principle.title
                              }
                            </strong>
                          </div>

                          <ArrowRight
                            size={15}
                          />

                        </motion.button>
                      );
                    }
                  )
                }

              </Reveal>


              <Reveal
                direction="right"
                className="eth-about-v9-player-detail-wrap"
              >

                <AnimatePresence
                  mode="wait"
                >

                  <motion.article
                    key={
                      currentPlayerPrinciple.id
                    }
                    className="eth-about-v9-player-detail"
                    initial={
                      reduceMotion
                        ? false
                        : {
                            opacity:
                              0,
                            x:
                              75,
                            y:
                              -24,
                            rotate:
                              1.3,
                            filter:
                              "blur(8px)"
                          }
                    }
                    animate={{
                      opacity:
                        1,
                      x:
                        0,
                      y:
                        0,
                      rotate:
                        0,
                      filter:
                        "blur(0px)"
                    }}
                    exit={
                      reduceMotion
                        ? undefined
                        : {
                            opacity:
                              0,
                            x:
                              -55,
                            y:
                              24,
                            rotate:
                              -1.1,
                            filter:
                              "blur(6px)"
                          }
                    }
                    transition={{
                      duration:
                        0.33
                    }}
                  >

                    <div className="eth-about-v9-player-detail-top">

                      <span>
                        {
                          currentPlayerPrinciple.number
                        }
                      </span>

                      <div>
                        <CurrentPlayerIcon
                          size={27}
                        />
                      </div>

                    </div>


                    <small>
                      OUR BUILD STANDARD
                    </small>

                    <h3>
                      {
                        currentPlayerPrinciple.title
                      }
                    </h3>

                    <p>
                      {
                        currentPlayerPrinciple.body
                      }
                    </p>


                    <div className="eth-about-v9-player-telemetry">

                      {
                        currentPlayerPrinciple.telemetry.map(
                          (
                            row
                          ) => (
                            <div
                              key={
                                row[0]
                              }
                            >
                              <span>
                                {
                                  row[0]
                                }
                              </span>

                              <strong>
                                {
                                  row[1]
                                }
                              </strong>
                            </div>
                          )
                        )
                      }

                    </div>


                    <div className="eth-about-v9-player-principle-line">
                      <HeartHandshake
                        size={17}
                      />

                      <span>
                        The measure of an early partnership is not how quickly the sales pitch ends. It is how clearly both sides understand the next practical step.
                      </span>
                    </div>

                  </motion.article>

                </AnimatePresence>

              </Reveal>

            </div>

          </div>
        </section>


        {/* ====================================================
            NOT MORALIZING
        ==================================================== */}

        <section className="eth-about-v9-section">

          <div className="eth-about-v9-shell">

            <Reveal
              direction="topLeft"
              className="eth-about-v9-section-head"
            >
              <span className="eth-about-v9-kicker">
                A PRACTICAL VIEW OF PROGRESS
              </span>

              <h2>
                Shipping software is progress.{" "}
                <span>
                  Earning adoption is another step.
                </span>
              </h2>

              <p>
                Ethosino has invested heavily in product development. The next job is different: establish relationships with operators, prove the onboarding and operating experience under real constraints, and improve the platform from that evidence. Both kinds of work matter, and they should not be confused with each other.
              </p>
            </Reveal>


            <div className="eth-about-v9-truth-table">

              {
                truthPairs.map(
                  (
                    pair,
                    index
                  ) => (
                    <Reveal
                      key={
                        pair.left
                      }
                      direction={
                        index %
                        2 ===
                        0
                          ? "left"
                          : "right"
                      }
                      delay={
                        index *
                        0.04
                      }
                    >
                      <div className="eth-about-v9-truth-row">

                        <div className="eth-about-v9-truth-index">
                          0{
                            index +
                            1
                          }
                        </div>

                        <div className="eth-about-v9-truth-left">
                          {
                            pair.left
                          }
                        </div>

                        <div className="eth-about-v9-truth-arrow">
                          <ArrowRight
                            size={16}
                          />
                        </div>

                        <div className="eth-about-v9-truth-right">
                          {
                            pair.right
                          }
                        </div>

                      </div>
                    </Reveal>
                  )
                )
              }

            </div>

          </div>
        </section>


        {/* ====================================================
            JOURNEY
        ==================================================== */}

        <section
          className="eth-about-v9-section eth-about-v9-section--panel"
          id="about-journey"
        >

          <div className="eth-about-v9-shell">

            <Reveal
              direction="topRight"
              className="eth-about-v9-section-head"
            >
              <span className="eth-about-v9-kicker">
                HOW THE PRODUCT TOOK SHAPE
              </span>

              <h2>
                One system led to the next.{" "}
                <span>
                  Now operators can evaluate it.
                </span>
              </h2>

              <p>
                There was no single moment when an isolated game became a B2B company. Each technical layer raised the next operational question: how does someone administer this, brand it, support it or expand it? The timeline follows that engineering progression without pretending the market-validation chapter has already been written.
              </p>
            </Reveal>


            <div className="eth-about-v9-journey">

              <div
                className="eth-about-v9-journey-line"
                aria-hidden="true"
              >
                <span />
              </div>


              {
                buildJourney.map(
                  (
                    step,
                    index
                  ) => {
                    const Icon =
                      step.icon;

                    const directions = [
                      "topLeft",
                      "topRight",
                      "left",
                      "right",
                      "bottomLeft",
                      "bottomRight"
                    ];

                    return (
                      <Reveal
                        key={
                          step.year
                        }
                        direction={
                          directions[
                            index
                          ]
                        }
                        delay={
                          index *
                          0.035
                        }
                      >
                        <motion.article
                          className="eth-about-v9-journey-card"
                          whileHover={
                            reduceMotion
                              ? undefined
                              : {
                                  y:
                                    -6,
                                  rotateX:
                                    1.5,
                                  rotateY:
                                    index %
                                    2 ===
                                    0
                                      ? -1.5
                                      : 1.5
                                }
                          }
                        >

                          <span className="eth-about-v9-journey-year">
                            {
                              step.year
                            }
                          </span>

                          <div className="eth-about-v9-journey-icon">
                            <Icon
                              size={20}
                            />
                          </div>

                          <h3>
                            {
                              step.title
                            }
                          </h3>

                          <p>
                            {
                              step.body
                            }
                          </p>

                        </motion.article>
                      </Reveal>
                    );
                  }
                )
              }

            </div>

          </div>
        </section>


        {/* ====================================================
            NOW
        ==================================================== */}

        <section className="eth-about-v9-section">

          <div className="eth-about-v9-shell">

            <div className="eth-about-v9-now-layout">

              <Reveal
                direction="left"
                className="eth-about-v9-now-copy"
              >
                <span className="eth-about-v9-kicker">
                  WHERE THE PLATFORM STANDS
                </span>

                <h2>
                  An extensive product foundation.{" "}
                  <span>
                    A focused commercial next step.
                  </span>
                </h2>

                <p>
                  Today the demonstration spans a player-facing casino experience, a growing first-party catalog and an operator administration environment. Those are concrete materials a prospective partner can inspect. They are a starting point for serious diligence, not a claim that every possible deployment requirement has already been fulfilled.
                </p>

                <p>
                  The immediate work is to turn the breadth of the platform into predictable operator onboarding: document requirements, test workflows together, complete the integrations a partner actually needs and establish a successful pilot before expanding scope.
                </p>


                <Link
                  to="/solutions/startup-operators"
                  className="eth-about-v9-inline-link"
                >
                  See the startup operator approach

                  <ArrowRight
                    size={15}
                  />
                </Link>
              </Reveal>


              <Reveal
                direction="bottomRight"
                className="eth-about-v9-now-system"
              >

                <div className="eth-about-v9-now-grid" />

                <div className="eth-about-v9-now-core">

                  <Orbit
                    size={27}
                  />

                  <span>
                    ETHOSINO
                  </span>

                  <strong>
                    PLATFORM FOUNDATION
                  </strong>

                </div>


                {
                  [
                    [
                      "EXPERIENCE",
                      "experience design",
                      Users
                    ],
                    [
                      "GAMES",
                      "in-house games",
                      Gamepad2
                    ],
                    [
                      "OPERATIONS",
                      "operations tooling",
                      WalletCards
                    ],
                    [
                      "QUALITY",
                      "testing culture",
                      ShieldCheck
                    ],
                    [
                      "PARTNERS",
                      "operator feedback",
                      Layers3
                    ],
                    [
                      "EVOLUTION",
                      "future releases",
                      Bot
                    ]
                  ].map(
                    (
                      node,
                      index
                    ) => {
                      const Icon =
                        node[2];

                      return (
                        <motion.div
                          className={`eth-about-v9-now-node eth-about-v9-now-node--${index + 1}`}
                          key={
                            node[0]
                          }
                          animate={
                            reduceMotion
                              ? {}
                              : {
                                  y: [
                                    0,
                                    index %
                                    2 ===
                                    0
                                      ? -8
                                      : 8,
                                    0
                                  ]
                                }
                          }
                          transition={{
                            duration:
                              4.2 +
                              index *
                              0.27,
                            repeat:
                              Infinity,
                            ease:
                              "easeInOut"
                          }}
                        >
                          <Icon
                            size={16}
                          />

                          <div>
                            <span>
                              {
                                node[0]
                              }
                            </span>

                            <strong>
                              {
                                node[1]
                              }
                            </strong>
                          </div>
                        </motion.div>
                      );
                    }
                  )
                }


                <span className="eth-about-v9-now-packet eth-about-v9-now-packet--one" />

                <span className="eth-about-v9-now-packet eth-about-v9-now-packet--two" />

              </Reveal>

            </div>

          </div>
        </section>


        {/* ====================================================
            AI BOUNDARY
        ==================================================== */}

        <section className="eth-about-v9-section eth-about-v9-section--panel">

          <div className="eth-about-v9-shell">

            <Reveal
              direction="topLeft"
              className="eth-about-v9-ai-principle"
            >

              <div className="eth-about-v9-ai-copy">

                <span className="eth-about-v9-kicker">
                  HOW WE DECIDE WHAT TO BUILD
                </span>

                <h2>
                  New ideas are easy.{" "}
                  <span>
                    Useful releases have to earn their place.
                  </span>
                </h2>

                <p>
                  The company has plenty of room to experiment with richer experiences and operator intelligence. But the next release should solve an observable problem: reduce setup friction, make an operator task clearer or improve reliability. An impressive feature that creates more support work than value is not automatically progress.
                </p>


                <div className="eth-about-v9-ai-thesis">
                  <BrainCircuit
                    size={18}
                  />

                  <strong>
                    Show the need. Build the change. Test the result.
                  </strong>
                </div>

              </div>


              <div className="eth-about-v9-ai-boundaries">

                {
                  [
                    [
                      Check,
                      "UNDERSTAND",
                      "identify the user problem before creating another feature",
                      "yes"
                    ],
                    [
                      Check,
                      "DEMONSTRATE",
                      "show an actual workflow rather than a marketing claim",
                      "yes"
                    ],
                    [
                      X,
                      "VAGUE PROMISES",
                      "avoid describing unfinished integrations as production-ready",
                      "no"
                    ],
                    [
                      X,
                      "UNTESTED RELEASES",
                      "avoid shipping presentation without behavior validation",
                      "no"
                    ],
                    [
                      X,
                      "HIDDEN DEPENDENCIES",
                      "make external services and approvals explicit",
                      "no"
                    ],
                    [
                      X,
                      "FEATURE COUNT FIRST",
                      "do not mistake more capabilities for more operator value",
                      "no"
                    ]
                  ].map(
                    (
                      row
                    ) => {
                      const Icon =
                        row[0];

                      return (
                        <div
                          className={`eth-about-v9-ai-boundary eth-about-v9-ai-boundary--${row[3]}`}
                          key={
                            row[1]
                          }
                        >
                          <span>
                            <Icon
                              size={12}
                            />
                          </span>

                          <div>
                            <strong>
                              {
                                row[1]
                              }
                            </strong>

                            <small>
                              {
                                row[2]
                              }
                            </small>
                          </div>
                        </div>
                      );
                    }
                  )
                }

              </div>

            </Reveal>

          </div>
        </section>


        {/* ====================================================
            FUTURE
        ==================================================== */}

        <section
          className="eth-about-v9-section"
          id="about-future"
        >

          <div className="eth-about-v9-shell">

            <Reveal
              direction="bottomLeft"
              className="eth-about-v9-future"
            >

              <div className="eth-about-v9-future-copy">

                <span className="eth-about-v9-kicker">
                  THE DIRECTION AHEAD
                </span>

                <h2>
                  From a founder-built product{" "}
                  <span>
                    to a durable software company.
                  </span>
                </h2>

                <p>
                  The next chapter depends on operator relationships, not another abstract roadmap. Ethosino aims to build repeatable onboarding, credible customer references, stronger partner integrations and a first-party gaming platform that improves with each deployment. The goal is an enduring B2B company whose customers can rely on the work.
                </p>


                <div className="eth-about-v9-future-quote">
                  <Sparkles
                    size={19}
                  />

                  <blockquote>
                    Build something worth deploying, support the people who depend on it, and improve it from real use.
                  </blockquote>
                </div>

              </div>


              <div className="eth-about-v9-future-compass">

                <div className="eth-about-v9-future-compass-grid" />

                <motion.div
                  className="eth-about-v9-future-ring eth-about-v9-future-ring--outer"
                  animate={
                    reduceMotion
                      ? {}
                      : {
                          rotate:
                            360
                        }
                  }
                  transition={{
                    duration:
                      30,
                    repeat:
                      Infinity,
                    ease:
                      "linear"
                  }}
                />

                <motion.div
                  className="eth-about-v9-future-ring eth-about-v9-future-ring--inner"
                  animate={
                    reduceMotion
                      ? {}
                      : {
                          rotate:
                            -360
                        }
                  }
                  transition={{
                    duration:
                      20,
                    repeat:
                      Infinity,
                    ease:
                      "linear"
                  }}
                />


                <div className="eth-about-v9-future-core">

                  <Target
                    size={28}
                  />

                  <span>
                    NEXT CHAPTER
                  </span>

                  <strong>
                    SUSTAINABLE B2B PRODUCT
                  </strong>

                </div>


                <div className="eth-about-v9-future-word eth-about-v9-future-word--one">
                  BUILD
                </div>

                <div className="eth-about-v9-future-word eth-about-v9-future-word--two">
                  TEST
                </div>

                <div className="eth-about-v9-future-word eth-about-v9-future-word--three">
                  PARTNER
                </div>

                <div className="eth-about-v9-future-word eth-about-v9-future-word--four">
                  IMPROVE
                </div>

              </div>

            </Reveal>

          </div>
        </section>


        {/* ====================================================
            FAQ
        ==================================================== */}

        <section
          className="eth-about-v9-section eth-about-v9-section--panel"
          id="about-faq"
        >

          <div className="eth-about-v9-shell">

            <div className="eth-about-v9-faq-layout">

              <Reveal
                direction="topLeft"
                className="eth-about-v9-faq-copy"
              >
                <span className="eth-about-v9-kicker">
                  ABOUT THE COMPANY
                </span>

                <h2>
                  Beyond the product demo:{" "}
                  <span>
                    the business behind it.
                  </span>
                </h2>

                <p>
                  Before asking for a commercial proposal, it is reasonable to ask who built Ethosino, why the product exists, what stage it is in and how the company works with its early customers. These answers address the business and its direction; the technical and pricing pages cover their own subjects in depth.
                </p>


                <div className="eth-about-v9-faq-callout">
                  <Compass
                    size={20}
                  />

                  <div>
                    <span>
                      OUR APPROACH
                    </span>

                    <strong>
                      Specific claims. Hands-on evaluation. Real partnerships.
                    </strong>
                  </div>
                </div>
              </Reveal>


              <Reveal
                direction="bottomRight"
                className="eth-about-v9-faq-list"
              >

                {
                  faqItems.map(
                    (
                      item,
                      index
                    ) => {
                      const open =
                        openFaq ===
                        index;

                      return (
                        <article
                          className={[
                            "eth-about-v9-faq-item",
                            open
                              ? "is-open"
                              : ""
                          ].join(
                            " "
                          )}
                          key={
                            item.q
                          }
                        >

                          <button
                            type="button"
                            onClick={() =>
                              setOpenFaq(
                                open
                                  ? -1
                                  : index
                              )
                            }
                            aria-expanded={
                              open
                            }
                          >
                            <span>
                              {
                                String(
                                  index +
                                  1
                                ).padStart(
                                  2,
                                  "0"
                                )
                              }
                            </span>

                            <strong>
                              {
                                item.q
                              }
                            </strong>

                            <motion.div
                              animate={{
                                rotate:
                                  open
                                    ? 180
                                    : 0
                              }}
                            >
                              <ChevronDown
                                size={17}
                              />
                            </motion.div>
                          </button>


                          <AnimatePresence
                            initial={false}
                          >
                            {
                              open
                                ? (
                                  <motion.div
                                    className="eth-about-v9-faq-answer"
                                    initial={{
                                      height:
                                        0,
                                      opacity:
                                        0
                                    }}
                                    animate={{
                                      height:
                                        "auto",
                                      opacity:
                                        1
                                    }}
                                    exit={{
                                      height:
                                        0,
                                      opacity:
                                        0
                                    }}
                                    transition={{
                                      duration:
                                        0.28
                                    }}
                                  >
                                    <p>
                                      {
                                        item.a
                                      }
                                    </p>
                                  </motion.div>
                                )
                                : null
                            }
                          </AnimatePresence>

                        </article>
                      );
                    }
                  )
                }

              </Reveal>

            </div>

          </div>
        </section>


        {/* ====================================================
            FINAL
        ==================================================== */}

        <section className="eth-about-v9-final">

          <div className="eth-about-v9-shell">

            <Reveal
              direction="bottomLeft"
              className="eth-about-v9-final-card"
            >

              <div className="eth-about-v9-final-grid" />

              <div className="eth-about-v9-final-beam" />


              <div className="eth-about-v9-final-copy">

                <span>
                  A FOUNDER-BUILT PLATFORM, OPEN TO PARTNERS
                </span>

                <h2>
                  A substantial foundation exists.{" "}
                  <strong>
                    Its next chapter needs real partners.
                  </strong>
                </h2>

                <p>
                  Ethosino has grown from an independent engineering project into an extensive B2B casino software platform. The invitation now is to explore the demonstration, bring a real operator scenario, identify the remaining requirements and shape a pilot around measurable outcomes.
                </p>


                <div className="eth-about-v9-final-actions">

                  <Link
                    to="/book-demo"
                    className="eth-about-v9-btn eth-about-v9-btn--primary eth-about-v9-btn--large"
                  >
                    Discuss an operator pilot

                    <ArrowRight
                      size={18}
                    />
                  </Link>


                  <Link
                    to="/provably-fair-engine"
                    className="eth-about-v9-btn eth-about-v9-btn--ghost eth-about-v9-btn--large"
                  >
                    Explore a core design principle
                  </Link>

                </div>

              </div>


              <div className="eth-about-v9-final-principles">

                <div className="eth-about-v9-final-principles-head">
                  <HeartHandshake
                    size={21}
                  />

                  <div>
                    <span>
                      THE COMPANY COMMITMENT
                    </span>

                    <strong>
                      Show the work. State the limits. Improve together.
                    </strong>
                  </div>
                </div>


                <div className="eth-about-v9-final-principles-list">

                  <span>
                    <Check
                      size={13}
                    />
                    Build and maintain the underlying product
                  </span>

                  <span>
                    <Check
                      size={13}
                    />
                    Describe current capabilities accurately
                  </span>

                  <span>
                    <Check
                      size={13}
                    />
                    Test workflows before expanding scope
                  </span>

                  <span>
                    <Check
                      size={13}
                    />
                    Make third-party dependencies explicit
                  </span>

                  <span>
                    <Check
                      size={13}
                    />
                    Prioritize feedback from real operators
                  </span>

                </div>

              </div>

            </Reveal>

          </div>
        </section>

      </main>


      <Footer />

    </Fragment>
  );
};


export default OperatorCompanyHelper;
